import {
  paymentMethodFromCardReadMode,
  tpePaymentMethodLabels,
} from "cadendar-shared";
import type { TpePaymentMethod } from "cadendar-shared";
import { useState } from "react";
import { CiLock, CiUnlock } from "react-icons/ci";
import { TpePaymentMethodButtons } from "./TpePaymentMethodButtons.tsx";

function formatMoneyCents(value: number) {
  return `${(value / 100).toFixed(2).replace(".", ",")} €`;
}

function formatCentsForInput(value: number) {
  return (value / 100).toFixed(2).replace(".", ",");
}

function parseMoneyToCents(value: string) {
  const parsed = Number(value.trim().replace(/\s+/g, "").replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  const cents = Math.round(parsed * 100);
  return cents <= 999999999999 ? cents : null;
}

export function TpePaymentCorrectionControls({
  payment,
  onSetPaymentMethod,
  onUnlockForCorrection,
  onCancelCorrection,
  onSaveCorrection,
  disabled,
}: {
  payment: {
    id: string;
    amount_cents: number;
    accounting_amount_cents: number;
    payment_method: TpePaymentMethod | null;
    payment_method_locked_at: Date | string | null;
    card_read_mode: string | null;
  };
  onSetPaymentMethod: (
    paymentId: string,
    method: TpePaymentMethod,
  ) => Promise<void>;
  onUnlockForCorrection: (paymentId: string) => Promise<void>;
  onCancelCorrection: (paymentId: string) => Promise<void>;
  onSaveCorrection: (options: {
    paymentId: string;
    paymentMethod: TpePaymentMethod;
    accountingAmountCents: number;
  }) => Promise<void>;
  disabled: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftAmount, setDraftAmount] = useState(() =>
    formatCentsForInput(payment.accounting_amount_cents),
  );
  const [draftMethod, setDraftMethod] = useState<TpePaymentMethod | null>(
    payment.payment_method,
  );
  const [amountError, setAmountError] = useState<string | null>(null);
  const suggestedMethod = paymentMethodFromCardReadMode(payment.card_read_mode);
  const methodDiffersFromTerminalSuggestion =
    payment.payment_method &&
    suggestedMethod &&
    payment.payment_method !== suggestedMethod;

  const startCorrection = async () => {
    try {
      await onUnlockForCorrection(payment.id);
      setDraftAmount(formatCentsForInput(payment.accounting_amount_cents));
      setDraftMethod(payment.payment_method);
      setAmountError(null);
      setIsEditing(true);
    } catch {}
  };

  const cancelCorrection = async () => {
    try {
      await onCancelCorrection(payment.id);
      setAmountError(null);
      setIsEditing(false);
    } catch {}
  };

  const saveCorrection = async () => {
    const accountingAmountCents = parseMoneyToCents(draftAmount);
    if (!accountingAmountCents) {
      setAmountError("Le montant doit être un nombre strictement positif.");
      return;
    }
    if (!draftMethod) {
      return;
    }
    try {
      await onSaveCorrection({
        paymentId: payment.id,
        paymentMethod: draftMethod,
        accountingAmountCents,
      });
      setAmountError(null);
      setIsEditing(false);
    } catch {}
  };

  if (!payment.payment_method) {
    return (
      <TpePaymentMethodButtons
        value={null}
        suggestedValue={suggestedMethod}
        onValueChange={(method) => {
          void onSetPaymentMethod(payment.id, method).catch(() => undefined);
        }}
        disabled={disabled}
        ariaLabel="Mode de paiement"
      />
    );
  }

  if (!isEditing && payment.payment_method_locked_at) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
          {tpePaymentMethodLabels[payment.payment_method]}
        </span>
        {methodDiffersFromTerminalSuggestion ? (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
            Le TPE indique {tpePaymentMethodLabels[suggestedMethod]}
          </span>
        ) : null}
        <button
          type="button"
          onClick={startCorrection}
          disabled={disabled}
          title="Déverrouiller"
          aria-label="Déverrouiller"
          className="rounded border border-slate-300 p-1 text-slate-700 disabled:opacity-50"
        >
          <CiLock aria-hidden="true" className="size-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={cancelCorrection}
          disabled={disabled}
          title="Verrouiller"
          aria-label="Verrouiller"
          className="rounded border border-slate-300 p-1 text-slate-700 disabled:opacity-50"
        >
          <CiUnlock aria-hidden="true" className="size-5" />
        </button>
      </div>
      <input
        type="text"
        inputMode="decimal"
        value={draftAmount}
        onChange={(event) => setDraftAmount(event.target.value)}
        aria-label="Montant corrigé"
        disabled={disabled}
        className="w-24 rounded border border-slate-300 px-2 py-1 text-sm disabled:opacity-50"
      />
      <TpePaymentMethodButtons
        value={draftMethod}
        onValueChange={setDraftMethod}
        disabled={disabled}
        ariaLabel="Mode de paiement corrigé"
      />
      {amountError ? (
        <p className="text-xs text-red-700">{amountError}</p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={saveCorrection}
          disabled={disabled || !draftMethod}
          className="rounded bg-slate-900 px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          Enregistrer
        </button>
        <button
          type="button"
          onClick={cancelCorrection}
          disabled={disabled}
          className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 disabled:opacity-50"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

export function TpePaymentAmount({
  amountCents,
  accountingAmountCents,
  originalAmountLabel = "TPE",
}: {
  amountCents: number;
  accountingAmountCents: number;
  originalAmountLabel?: string;
}) {
  return (
    <div>
      <span>{formatMoneyCents(accountingAmountCents)}</span>
      {accountingAmountCents !== amountCents ? (
        <span className="block text-xs text-slate-500">
          {originalAmountLabel} : {formatMoneyCents(amountCents)}
        </span>
      ) : null}
    </div>
  );
}
